<!-- frontend/src/components/charts/BarChart.vue -->
<template>
  <div class="relative">
    <!-- Загрузка -->
    <div v-if="isLoading" class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
      <div class="text-center">
        <div class="loading-spinner mx-auto mb-2"></div>
        <p class="text-sm text-gray-500">Загрузка данных...</p>
      </div>
    </div>

    <!-- Ошибка -->
    <div v-else-if="error" class="h-64 flex items-center justify-center">
      <div class="text-center">
        <ExclamationTriangleIcon class="h-8 w-8 mx-auto text-red-400 mb-2" />
        <p class="text-gray-600">Ошибка загрузки диаграммы</p>
        <p class="text-sm text-gray-500 mt-1">{{ error }}</p>
        <button @click="loadData" class="mt-2 text-sm text-blue-600 hover:text-blue-800">
          Попробовать снова
        </button>
      </div>
    </div>

    <!-- Пустые данные -->
    <div v-else-if="!chartData || chartData.length === 0" class="h-64 flex items-center justify-center">
      <div class="text-center">
        <ChartBarIcon class="h-8 w-8 mx-auto text-gray-400 mb-2" />
        <p class="text-gray-500">Нет данных для отображения</p>
      </div>
    </div>

    <!-- Диаграмма -->
    <div v-else :style="{ height: height + 'px' }">
      <canvas ref="chartCanvas"></canvas>
    </div>

    <!-- Настройки диаграммы -->
    <div v-if="showControls && chartData && chartData.length > 0" class="mt-4 flex items-center justify-between">
      <!-- Переключатель типа диаграммы -->
      <div class="flex items-center space-x-2">
        <span class="text-sm text-gray-500">Тип:</span>
        <select
          v-model="chartType"
          @change="updateChartType"
          class="text-sm border border-gray-300 rounded px-2 py-1"
        >
          <option value="bar">Столбцы</option>
          <option value="line">Линия</option>
          <option value="horizontalBar">Горизонтальные столбцы</option>
        </select>
      </div>

      <!-- Информация о данных -->
      <div class="text-xs text-gray-500">
        Записей: {{ chartData.length }} |
        Всего: {{ totalValue.toLocaleString('ru-RU') }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { ChartBarIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import { useChart, chartColors } from '@/composables/useChart'

const props = defineProps({
  data: {
    type: Array,
    default: () => []
  },
  labelField: {
    type: String,
    default: '_id'
  },
  valueField: {
    type: String,
    default: 'count'
  },
  title: {
    type: String,
    default: 'Диаграмма'
  },
  height: {
    type: Number,
    default: 300
  },
  showControls: {
    type: Boolean,
    default: true
  },
  autoLoad: {
    type: Boolean,
    default: true
  },
  // Новые props для кастомизации
  colors: {
    type: Array,
    default: () => chartColors.mixed
  },
  animated: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['chartClick', 'dataLoaded', 'error'])

// Состояние
const isLoading = ref(false)
const error = ref(null)
const chartData = ref([])
const chartCanvas = ref(null)
const chartType = ref('bar')

// Конфигурация диаграммы
const chartConfig = computed(() => {
  if (!chartData.value || chartData.value.length === 0) {
    return null
  }

  // Сохраняем полные названия для подсказок
  const fullLabels = chartData.value.map(item => {
    const label = item[props.labelField]
    return label || 'Не указано'
  })

  // Для подписей используем сокращенные названия
  const labels = fullLabels.map(label => {
    return typeof label === 'string' && label.length > 30
      ? label.substring(0, 30) + '...'
      : label
  })

  const values = chartData.value.map(item => item[props.valueField] || 0)

  const baseConfig = {
    type: chartType.value === 'horizontalBar' ? 'bar' : chartType.value,
    data: {
      labels,
      datasets: [{
        label: props.title,
        data: values,
        backgroundColor: props.colors.slice(0, values.length),
        borderColor: props.colors.slice(0, values.length).map(color => color + 'CC'),
        borderWidth: 1,
        borderRadius: chartType.value === 'bar' ? 4 : 0,
        borderSkipped: false,
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: chartType.value === 'horizontalBar' ? 'y' : 'x',
      interaction: {
        intersect: false,
        mode: 'index'
      },
      animation: props.animated ? {
        duration: 1000,
        easing: 'easeOutQuart'
      } : false,
      onClick: (event, elements) => {
        if (elements.length > 0) {
          const index = elements[0].index
          const clickedData = chartData.value[index]
          emit('chartClick', { data: clickedData, index })
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          titleColor: 'white',
          bodyColor: 'white',
          borderColor: 'rgba(255, 255, 255, 0.2)',
          borderWidth: 1,
          cornerRadius: 8,
          padding: 16,
          titleFont: {
            size: 14,
            weight: 'bold'
          },
          bodyFont: {
            size: 13
          },
          displayColors: true,
          boxWidth: 12,
          boxHeight: 12,
          boxPadding: 4,
          callbacks: {
            title: function(context) {
              // Показываем полное название в заголовке подсказки
              const index = context[0].dataIndex
              return fullLabels[index]
            },
            label: function(context) {
              const value = context.parsed.y || context.parsed.x
              return `${props.title}: ${value.toLocaleString('ru-RU')}`
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.1)',
            lineWidth: 1
          },
          ticks: {
            color: '#6B7280',
            font: {
              size: 11
            },
            callback: function(value) {
              return value.toLocaleString('ru-RU')
            }
          }
        },
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: '#6B7280',
            font: {
              size: 11
            },
            maxRotation: 45,
            minRotation: 0
          }
        }
      }
    }
  }

  return baseConfig
})

// Используем композицию для Chart.js
const { updateChart, chart } = useChart(chartCanvas, chartConfig)

// Вычисляемые свойства
const totalValue = computed(() => {
  return chartData.value.reduce((sum, item) => sum + (item[props.valueField] || 0), 0)
})

// Методы
const loadData = async () => {
  if (props.data && props.data.length > 0) {
    chartData.value = props.data
    emit('dataLoaded', chartData.value)
    return
  }

  if (!props.autoLoad) return

  try {
    isLoading.value = true
    error.value = null

    // Генерируем тестовые данные
    const testData = generateTestData()
    chartData.value = testData

    emit('dataLoaded', testData)
    console.log('✅ Тестовые данные для столбчатой диаграммы созданы')
  } catch (err) {
    console.error('❌ Ошибка загрузки данных:', err)
    error.value = err.message || 'Ошибка загрузки данных'
    emit('error', error.value)
  } finally {
    isLoading.value = false
  }
}

const generateTestData = () => {
  const categories = [
    'Новые лиды', 'В работе', 'Квалифицированы',
    'Переговоры', 'Заключены', 'Отклонены'
  ]

  return categories.map(category => ({
    [props.labelField]: category,
    [props.valueField]: Math.floor(Math.random() * 100) + 10
  }))
}

const updateChartType = () => {
  if (chart.value) {
    chart.value.destroy()
    nextTick(() => {
      const newConfig = chartConfig.value
      if (newConfig && chartCanvas.value) {
        updateChart(newConfig.data)
      }
    })
  }
}

// Обновление диаграммы при изменении данных
watch(chartData, (newData) => {
  if (newData && newData.length > 0 && chartConfig.value) {
    updateChart(chartConfig.value.data)
  }
}, { deep: true })

// Обновление при изменении props.data
watch(() => props.data, (newData) => {
  if (newData && newData.length > 0) {
    chartData.value = newData
  }
}, { immediate: true, deep: true })

// Инициализация
onMounted(() => {
  loadData()
})

// Экспорт методов для родительского компонента
defineExpose({
  loadData,
  refreshChart: () => updateChart(chartConfig.value?.data),
  exportChart: () => chart.value?.toBase64Image(),
  chartData: chartData
})
</script>